import { useJSONFetcher, JSONFetcherOptions } from "@outplayed/json-fetcher";

// ─────────────────────────────────────────────────────────────────────
// League Classic per-champion detail — shared data layer (PREVIEW)
// ─────────────────────────────────────────────────────────────────────
//
// All League Classic champion data + images, produced by the champion-
// information tool into the `classic-champions/` path. Files:
//   classic-champions.json  list manifest: id → [slugId, displayName]
//   {id}.json               per-champion detail (this file's ClassicChampionDetail)
//   {id}.webp               champion icon (grid + build-page header)
//   {id}_p.webp             passive icon
//   {id}_{q,w,e,r}.webp     ability icons
//
// The list manifest drives the grid and slug → id resolution; the detail
// JSON drives the build page.

export const LEAGUE_CLASSIC_CHAMPION_DETAIL_BASE_URL =
  "https://static.bigbrain.gg/cdragon-custom/league_classic/classic-champions/";

// Ability slot keys, in display order (passive first, then Q/W/E/R).
export type ClassicAbilityKey = "p" | "q" | "w" | "e" | "r";
export const CLASSIC_ABILITY_KEYS: ClassicAbilityKey[] = ["p", "q", "w", "e", "r"];

export interface ClassicChampionPassive {
  name: string;
  description: string;
}

export interface ClassicChampionSpell {
  spellKey: string; // "q" | "w" | "e" | "r"
  name: string;
  description: string;
  dynamicDescription?: string;
  // Raw templated fields (contain @Cost@ / @Cooldown@ tokens).
  cost?: string;
  cooldown?: string;
  // Per-rank values (length 6, trailing entries pad unused ranks).
  costCoefficients?: number[];
  cooldownCoefficients?: number[];
}

export interface ClassicChampionTagInfo {
  championTagPrimary: string;
  championTagSecondary: string;
}

// Riot-style 3-point playstyle ratings (each value is 1–3).
export interface ClassicPlaystyleInfo {
  damage: number;
  durability: number;
  crowdControl: number;
  mobility: number;
  utility: number;
}

// Only the fields the FE uses today are typed; the cdragon export carries
// much more (coefficients, effect amounts, etc.) that we ignore for now.
export interface ClassicChampionDetail {
  id: number;
  name: string;
  alias: string;
  title: string;
  shortBio?: string;
  roles?: string[];
  championTagInfo?: ClassicChampionTagInfo;
  playstyleInfo?: ClassicPlaystyleInfo;
  passive: ClassicChampionPassive;
  spells: ClassicChampionSpell[];
}

// Champion icon, keyed by champion id — used by both the grid and the
// build-page header.
export function getLeagueClassicChampionImg(championId: number | string) {
  return `${LEAGUE_CLASSIC_CHAMPION_DETAIL_BASE_URL}${championId}.webp`;
}

// Ability icon derived from champion id + slot key, e.g. 60001_q.webp.
export function getLeagueClassicAbilityIcon(championId: number | string, key: ClassicAbilityKey) {
  return `${LEAGUE_CLASSIC_CHAMPION_DETAIL_BASE_URL}${championId}_${key}.webp`;
}

// Resolves an ability's display info by slot key. "p" reads the passive;
// q/w/e/r match a spell by spellKey. Returns the static `description`
// (clean prose), not `dynamicDescription` (carries @placeholders@/tags).
export function getClassicAbility(detail: ClassicChampionDetail | undefined, key: ClassicAbilityKey) {
  if (key === "p") {
    return { name: detail?.passive?.name, description: detail?.passive?.description };
  }
  const spell = detail?.spells?.find((s) => s.spellKey === key);
  return { name: spell?.name, description: spell?.description };
}

// ─────────────────────────────────────────────────────────────────────
// Champion list manifest — classic-champions.json
// ─────────────────────────────────────────────────────────────────────
//
// Record keyed by champion id → [slugId, displayName]. Replaces the old
// champions/champions.json manifest as the source for the classic champions
// grid and for slug → id resolution on the build page.
//   [0] slugId      — the champion segment of the build-page URL ("annie")
//   [1] displayName — the name shown in the grid ("Annie")
export type LeagueClassicChampionEntry = [slugId: string, displayName: string];
export type LeagueClassicChampionList = Record<string, LeagueClassicChampionEntry>;

// The build-page URL slug is "classic-" + the manifest slugId, e.g.
// "annie" → "classic-annie". This is the `:champion` route param.
export function getLeagueClassicChampionSlug(slugId: string) {
  return `classic-${slugId}`;
}

// Reverse resolution: URL slug → champion id. Returns undefined while the
// manifest is loading or if the slug doesn't resolve (e.g. a hand-typed URL).
export function findLeagueClassicChampionIdBySlug(
  list: LeagueClassicChampionList | undefined,
  slug: string | undefined,
): number | undefined {
  if (!list || !slug) return undefined;
  const match = Object.entries(list).find(([, entry]) => getLeagueClassicChampionSlug(entry[0]) === slug);
  return match ? Number(match[0]) : undefined;
}

// Fetches the champion list manifest.
export function useLeagueClassicChampionList<K = void>(options?: JSONFetcherOptions<LeagueClassicChampionList, K>) {
  const { requestInit, customCacheKey, skip = false, ssr = false, onCompleted, onError } = options || {};

  const url = `${LEAGUE_CLASSIC_CHAMPION_DETAIL_BASE_URL}classic-champions.json`;
  return useJSONFetcher<LeagueClassicChampionList, K>(url, {
    requestInit,
    customCacheKey,
    skip,
    ssr,
    onCompleted,
    onError,
  });
}

// Fetches one champion's detail JSON by id. Skips while the id is unknown
// (e.g. before the manifest resolves the slug) so we never request
// `.../undefined.json`.
export function useLeagueClassicChampionDetail<K = void>(
  championId: number | undefined,
  options?: JSONFetcherOptions<ClassicChampionDetail, K>,
) {
  const { requestInit, customCacheKey, skip = false, ssr = false, onCompleted, onError } = options || {};

  const effectiveSkip = skip || !championId;
  const url = `${LEAGUE_CLASSIC_CHAMPION_DETAIL_BASE_URL}${championId}.json`;
  return useJSONFetcher<ClassicChampionDetail, K>(url, {
    requestInit,
    customCacheKey,
    skip: effectiveSkip,
    ssr,
    onCompleted,
    onError,
  });
}
